import React, { useState } from "react";

import moment from "moment-timezone";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faTrashAlt,
} from "@fortawesome/pro-light-svg-icons";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { useSetFlash } from "providers/WithFlashes";

import Panel from "ui/Panel";
import Loading from "ui/Loading";
import Grid from "ui/Grid";
import Button from "ui/Button";
import PanelSection from "ui/PanelSection";

import { formatDateShort } from "utils/format";

import {
  OAuthApplicationsDocument,
  useOAuthApplicationsQuery,
} from "./gql/Query.generated";
import { useRevokeOAuthApplicationMutation } from "./gql/Mutation.generated";
import { useRevokeAllOrganizationOauthTokensMutation } from "./gql/RevokeAllOauthTokens.generated";
import pluralize from "pluralize";

const SCOPE_LABELS: Record<string, string> = {
  restricted_read: "Restricted Read",
  read: "Read",
  write: "Write",
};

function formatScopes(scopes: string): string {
  return scopes
    .split(" ")
    .map((s) => SCOPE_LABELS[s] || s)
    .join(", ");
}

export const OAuthAdminPanel = ({
  organizationSlug,
}: {
  organizationSlug: string;
}) => {
  const setFlash = useSetFlash();
  const [revokeAll, { loading: revokeLoading }] =
    useRevokeAllOrganizationOauthTokensMutation();

  const handleRevokeAll = () => {
    if (
      !window.confirm(
        "Are you sure you want to revoke all OAuth tokens for this organization? This will disconnect all authorized OAuth applications for all members.",
      )
    ) {
      return;
    }
    revokeAll({
      variables: { organizationSlug },
      onCompleted: (data) => {
        const count = data.revokeAllOrganizationOauthTokens.tokensRevoked;
        setFlash(
          "success",
          `Successfully revoked ${pluralize("token", count, true)}.`,
        );
      },
      onError: (error) => {
        setFlash("error", error.message);
      },
      refetchQueries: [
        {
          query: OAuthApplicationsDocument,
          variables: { organizationSlug },
        },
      ],
    });
  };

  return (
    <Panel title="Revoke All OAuth Tokens">
      <PanelSection>
        <p>
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          Revoke all OAuth tokens and grants for every member in this
          organization. This will disconnect all authorized OAuth applications.
        </p>
        <button
          onClick={handleRevokeAll}
          disabled={revokeLoading}
          className="btn btn-danger"
        >
          Revoke All
        </button>
      </PanelSection>
    </Panel>
  );
};

export const OAuthApplicationsPanel = ({
  organizationSlug,
}: {
  organizationSlug: string;
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const { data, loading, error } = useOAuthApplicationsQuery({
    variables: { organizationSlug },
  });
  const [revokeApplication] = useRevokeOAuthApplicationMutation();

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  return (
    <>
      <Panel title="Authorized OAuth Applications">
        <Grid
          data={data.getOauthApplications}
          defaultSortBy="createdAt"
          noRowsText="You have not authorized any OAuth applications for this organization."
          columns={[
            {
              field: "name",
              header: "Application",
              width: "1fr",
            },
            {
              field: "scopes",
              header: "Scopes",
              width: "200px",
              renderer: function ScopesCell({ fieldData }) {
                return <>{formatScopes(fieldData)}</>;
              },
            },
            {
              field: "lastUsedAt",
              header: "Last Used At",
              width: "120px",
              nullValue: "-",
              renderer: function LastUsedAtCell({ fieldData }) {
                return formatDateShort(moment.unix(fieldData));
              },
            },
            {
              field: "createdAt",
              header: "Created At",
              width: "120px",
              renderer: function CreatedAtCell({ fieldData }) {
                return formatDateShort(moment.unix(fieldData));
              },
            },
            {
              field: "id",
              header: "",
              width: "40px",
              renderer: function RevokeCell({ rowData }) {
                return (
                  <Button
                    onClick={() => {
                      setErrorMessage("");
                      if (
                        window.confirm(`Revoke access for "${rowData.name}"?`)
                      ) {
                        revokeApplication({
                          variables: {
                            applicationId: rowData.id,
                            organizationSlug: rowData.organizationSlug,
                          },
                          onError: (error) => {
                            setErrorMessage(error.message);
                          },
                          refetchQueries: [
                            {
                              query: OAuthApplicationsDocument,
                              variables: { organizationSlug },
                            },
                          ],
                        });
                      }
                    }}
                    bare
                    title="Revoke"
                  >
                    <FontAwesomeIcon
                      icon={faTrashAlt}
                      className="text-[#CA1515] cursor-pointer"
                    />
                  </Button>
                );
              },
            },
          ]}
        />
      </Panel>
      {errorMessage && (
        <div className="text-[#C22426]">
          <FontAwesomeIcon icon={faExclamationCircle} /> {errorMessage}
        </div>
      )}
    </>
  );
};
